import { Collapse } from "@mui/material";
import React from "react";
import { ProductCard } from "@/features/product/ProductCard";
import { useRecommendedProducts } from "@/features/recommended-products";
import { TransitionGroup } from "react-transition-group";
import { useCartUiActions } from "./cartStore";
import {
  CartAddBodyWithTracking,
  shopifyCartApi,
} from "@/services/shopifyCart";
import { SizedImage } from "@/components/SizedImage";
import { Button } from "@/components/Button";
import { DroppsProduct, productSelect } from "@/store";
import { scentMapping } from "@/helpers/scentMapping";
import { cn } from "@/helpers/utils";

export interface RecommendedProductListProps {
  showShopNow: boolean;
  cardsShown?: number;
  headline: string;
  subHeadline?: string;
  variationMode?: boolean;
}

export const RecommendedProductCardList = ({
  cardsShown = 4,
  showShopNow,
  headline,
  subHeadline,
  variationMode = false,
}: RecommendedProductListProps) => {
  const recommendedProducts = useRecommendedProducts();
  const topProducts = recommendedProducts.slice(0, cardsShown);

  if (topProducts.length === 0) {
    return null;
  }

  return (
    <div className="recommended-products flex flex-col px-1">
      {headline ? (
        <div className="px-4 py-6 text-center">
          <h3
            className={
              variationMode
                ? "text-xl font-bold mb-0 text-black"
                : "title text-xl"
            }
          >
            {headline}
          </h3>
          {subHeadline ? <p className="body pt-2">{subHeadline}</p> : null}
        </div>
      ) : null}
      <TransitionGroup component={"ul"}>
        {topProducts.map((x) => (
          <Collapse key={x.id} component={"li"}>
            <hr className="divider-x mx-4 my-0" />
            <RecommendedProductCard
              showShopNow={showShopNow}
              product={x}
              variationMode={variationMode}
            />
          </Collapse>
        ))}
      </TransitionGroup>
    </div>
  );
};

const RecommendedProductCard = ({
  showShopNow,
  product,
  variationMode = false,
}: {
  showShopNow: boolean;
  product: DroppsProduct;
  variationMode?: boolean;
}) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = productSelect.defaultVariant(product);
  const productUrl = productSelect.url(product);

  const productCardProduct = {
    featuredImage: productSelect.featuredImage(product),
    featuredText: product.featuredQuote,
    title: product.title,
    price: undefined,
    ...(variationMode ? { product } : {}),
  };

  const closeCart = () => cartActions.closeDrawer();

  if (variationMode) {
    const scent = productSelect.scent(productCardProduct);
    const scentName = scent || product.scent?.name;
    const scentBgColor = product.scent?.color;
    const scentBgClass = scentName
      ? scentMapping[scentName]?.bgColor
      : undefined;

    return (
      <div className="p-4">
        <div className="flex flex-row space-x-4">
          <div className="flex w-20 shrink-0 flex-col">
            <a
              href={productUrl}
              onClick={closeCart}
              className="[&>img]:max-w-full"
            >
              <SizedImage
                src={productCardProduct.featuredImage.url}
                alt={productCardProduct.title}
                height={80}
                width={80}
                loading="lazy"
              />
            </a>
            <div className="pt-1">
              <Button
                className="text-sm font-semibold uppercase tracking-wider hover:border-primary-800 hover:bg-primary-800 hover:text-white py-3 px-[16.5px] h-auto border"
                variant="outline"
                size="sm"
                onClick={async () => {
                  const payload: CartAddBodyWithTracking = {
                    items: [{ id: variant.id, quantity: 1 }],
                    sourceModule: "cart_recommendation",
                  };
                  shopifyCartAdder.mutate(payload);
                }}
                loading={shopifyCartAdder.isLoading}
              >
                ADD +
                <span className="sr-only"> {productCardProduct.title}</span>
              </Button>
            </div>
          </div>
          <div className="flex flex-1 flex-col space-y-2">
            <a href={productUrl} onClick={closeCart}>
              <h4
                data-id="product-card-title"
                className="title text-md font-bold tracking-[-1px] transition hover:underline"
              >
                {productSelect.name(productCardProduct).replace(/<br>/gi, " ")}
              </h4>
            </a>
            {scentName && (scentBgColor || scentBgClass) ? (
              <div
                className={cn(
                  "!mt-1 inline-flex max-w-fit items-center rounded-full px-3 py-1 font-myriad-pro-condensed text-sm font-semibold uppercase text-black",
                  !scentBgColor && scentBgClass
                )}
                style={
                  scentBgColor
                    ? { backgroundColor: scentBgColor }
                    : undefined
                }
                data-id="product-card-scent-name"
              >
                {scentName}
              </div>
            ) : scentName ? (
              <span
                className="mt-1 text-sm"
                data-id="product-card-scent-name"
              >
                {scentName}
              </span>
            ) : null}
            {productCardProduct.featuredText && (
              <p
                data-id="product-card-featured-text"
                className="body text-sm !mt-3"
              >
                {productCardProduct.featuredText}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProductCard
      className="!bg-transparent [&_.product-card-scent-name]:bg-white"
      productCardValue={productCardProduct}
      cover={
        <a href={productUrl} onClick={closeCart}>
          <div className="w-20 shrink-0 [&>img]:max-w-full">
            <SizedImage
              src={productCardProduct.featuredImage.url}
              alt={productCardProduct.title}
              height={80}
              width={80}
              loading="lazy"
            />
          </div>
        </a>
      }
      productTitle={
        <a href={productUrl} onClick={closeCart}>
          <h4
            data-id="product-card-title"
            className="title text-sm transition hover:underline"
          >
            {productSelect.name(productCardProduct).replace(/<br>/gi, " ")}
          </h4>
        </a>
      }
      compact={true}
      hoverable={false}
      rounded={false}
      actions={
        <div className="ml-24 flex items-center">
          <Button
            className="text-sm normal-case tracking-normal"
            variant="text"
            onClick={async () => {
              const payload: CartAddBodyWithTracking = {
                items: [{ id: variant.id, quantity: 1 }],
                sourceModule: "cart_recommendation",
              };
              shopifyCartAdder.mutate(payload);
            }}
            loading={shopifyCartAdder.isLoading}
          >
            Add to Cart{" "}
            <span className="sr-only">{productCardProduct.title}</span>
          </Button>
          {showShopNow && (
            <>
              <span className="mx-1.5">|</span>
              <Button
                className="text-sm normal-case tracking-normal"
                href={productUrl}
                variant="text"
                onClick={closeCart}
                aria-label={`Shop ${product.title}`}
              >
                Shop Now
              </Button>
            </>
          )}
        </div>
      }
    />
  );
};
